import { _ as __rest } from './_tslib.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { OPTIONS_BY_LANGUAGE, generateClasses, toKebabCase } from '@ids/tools';
import { s as styleInject } from './style-inject.es.js';
import { u as useId } from './use-id.js';
import { u as useDefaultValue } from './use-default-value.js';

var css_248z = "/*\n** Voxel Foundation Formats\n*/\n/*\n** Voxel Foundation Media Query Mixins\n*/\n.ids-likert-item {\n  -webkit-box-align: center;\n      -ms-flex-align: center;\n          align-items: center;\n  background: none;\n  border: none;\n  color: var(--ids_color_action_neutral_base);\n  cursor: pointer;\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  -webkit-box-orient: vertical;\n  -webkit-box-direction: normal;\n      -ms-flex-direction: column;\n          flex-direction: column;\n  font: var(--ids_textStyle_f02_s14_h20_wrg);\n}\n.ids-likert-item__face {\n  border: var(--ids_size_border_medium) solid transparent;\n  border-radius: 50%;\n  -webkit-box-sizing: border-box;\n          box-sizing: border-box;\n  margin-bottom: var(--ids_size_general_8x);\n}\n@media (max-width: 47.99rem) {\n  .ids-likert-item__face {\n    height: var(--ids_size_general_28x);\n    width: var(--ids_size_general_28x);\n  }\n}\n.ids-likert-item__name {\n  margin: 0 var(--ids_size_general_6x);\n}\n.ids-likert-item--selected {\n  font: var(--ids_textStyle_f02_s14_h20_wbd);\n}\n.ids-likert-item--selected .ids-likert-item__face {\n  border-color: #000;\n}\n.ids-likert-item--on-brand {\n  color: var(--ids_color_contrast_onBrand_primary);\n}\n.ids-likert-item--on-brand.ids-likert-item--selected .ids-likert-item__face {\n  border-color: var(--ids_color_contrast_onBrand_primary);\n}\n.ids-likert-item:hover {\n  font: var(--ids_textStyle_f02_s14_h20_wbd);\n}\n\n.ids-likert {\n  border: 0;\n  color: var(--ids_color_text_body_01);\n  display: block;\n  margin: 0;\n  overflow: hidden;\n  padding: 0;\n}\n.ids-likert__list {\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  -ms-flex-pack: distribute;\n      justify-content: space-around;\n  margin: var(--ids_size_general_28x) 0;\n}\n.ids-likert--on-brand .ids-label {\n  color: var(--ids_color_contrast_onBrand_primary);\n}";
styleInject(css_248z);

/**
 * @category Component
 *
 * @example
 * ```ts
 * import { IdsLikert } from '@ids/react';
 * ```
 */
const IdsLikert = _a => {
  var {
      id: externalId,
      className = '',
      children,
      contrast,
      gender,
      onChange,
      setValue: externalSetValue,
      value: externalValue,
      defaultValue
    } = _a,
    props = __rest(_a, ["id", "className", "children", "contrast", "gender", "onChange", "setValue", "value", "defaultValue"]);
  const {
    lang = 'pt'
  } = props;
  let options = [...OPTIONS_BY_LANGUAGE[lang]];
  options = options === null || options === void 0 ? void 0 : options.map(item => typeof item.value === 'string' ? item : Object.assign(Object.assign({}, item), {
    value: gender === 'female' ? item.value[1] : item.value[0]
  }));
  const id = useId(externalId, 'ids-react-likert');
  const [value, setValue] = useDefaultValue(defaultValue, externalValue, externalSetValue);
  const classes = generateClasses({
    'ids-likert': true,
    'ids-likert--on-brand': contrast === 'onBrand',
    [className]: className
  });
  function handleClickOption(optionKey) {
    if (setValue) {
      setValue(optionKey);
    }
    if (onChange) {
      onChange(optionKey);
    }
  }
  function renderOption(option) {
    const checked = value === option.key;
    const optionClasses = generateClasses({
      'ids-likert-item': true,
      'ids-likert-item--on-brand': contrast === 'onBrand',
      'ids-likert-item--selected': checked
    });
    return jsxs("button", Object.assign({
      type: "button",
      className: optionClasses,
      id: `${id}-${toKebabCase(option.key)}`,
      role: "radio",
      "aria-checked": checked,
      onClick: () => handleClickOption(option.key)
    }, {
      children: [jsx("img", {
        className: "ids-likert-item__face",
        "aria-hidden": "true",
        src: option.face,
        alt: ""
      }), jsx("span", Object.assign({
        className: "ids-likert-item__name"
      }, {
        children: option.value
      }))]
    }), option.key);
  }
  return jsxs("fieldset", Object.assign({
    className: classes
  }, props, {
    id: id
  }, {
    children: [jsx("legend", Object.assign({
      className: "ids-label"
    }, {
      children: children
    })), jsx("div", Object.assign({
      className: "ids-likert__list"
    }, {
      children: options.map(renderOption)
    }))]
  }));
};

export { IdsLikert as I };
//# sourceMappingURL=likert.js.map
